import qs from 'qs';
import { createResource } from 'react-resource-router';

import {
  TestCase,
  type TestCaseResponse,
} from 'src/components/pipelines/models/TestReport';
import { User } from 'src/components/pipelines/models/User';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { SentryPipelinesTeam } from 'src/components/pipelines/utils/sentry';

import { FixFlakyTestStatus } from '../components/test-management';
import { PAGE_LEN } from '../components/test-management/utils';
import {
  FULLY_QUALIFIED_NAME_QUERY_PARAM,
  LABELS_QUERY_PARAM,
  PAGE_QUERY_PARAM,
  PAGELEN_QUERY_PARAM,
  SORT_QUERY_PARAM,
  TEST_TYPE,
} from '../constants';

import { fetchData } from './utils';

export type Query = {
  page?: string;
  pagelen?: string;
  fullyQualifiedName?: string;
  labels?: string;
  sort?: string;
  testType?: string;
};

export function getTestCasesUrl(repositoryFullSlug: string, query?: Query) {
  const url = `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/tests`;

  const queryParams = qs.stringify(query || {}, {
    skipNulls: true,
    addQueryPrefix: true,
  });

  return `${url}${queryParams}`;
}

export function getTestCaseUrl(repositoryFullSlug: string, testUuid: string) {
  return `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/tests/${encodeURIComponent(testUuid)}`;
}

export function getFixFlakyTestStatusUrl(
  repositoryFullSlug: string,
  testUuid: string
) {
  return `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/tests/${encodeURIComponent(
    testUuid
  )}/actions/fix-flaky-test`;
}

function buildTestCasesQuery(query: any): Query {
  return {
    [PAGE_QUERY_PARAM]: query.page || '1',
    [PAGELEN_QUERY_PARAM]: PAGE_LEN.toString(),
    [FULLY_QUALIFIED_NAME_QUERY_PARAM]: query.fullyQualifiedName,
    [LABELS_QUERY_PARAM]: query.labels,
    [SORT_QUERY_PARAM]: query.sort || '-metrics.failureRate',
    [TEST_TYPE]: query.testType,
  };
}

export const loadTestCasesResource = createResource({
  type: 'pipelinesTestCases',
  getKey: ({ match, query }) =>
    getTestCasesUrl(
      match.params.repositoryFullSlug!,
      buildTestCasesQuery(query)
    ),
  maxAge: 0,
  getData: async ({ match, query }) => {
    const testCasesData = await fetchData(
      getTestCasesUrl(
        match.params.repositoryFullSlug!,
        buildTestCasesQuery(query)
      ),
      'test_cases_resource_exception',
      {},
      'json',
      SentryPipelinesTeam.Mangalyaan
    );

    return {
      page: testCasesData.page,
      pagelen: testCasesData.pagelen,
      size: testCasesData.size,
      values: testCasesData.values.map(
        (testCase: TestCaseResponse, index: number) =>
          new TestCase(testCase, index)
      ),
    };
  },
});

export const loadTestCaseResource = createResource({
  type: 'pipelinesTestCase',
  getKey: ({ match }) =>
    getTestCaseUrl(match.params.repositoryFullSlug!, match.params.testUuid!),
  maxAge: 60000,
  getData: async ({ match }) => {
    const testCaseData = await fetchData(
      getTestCaseUrl(match.params.repositoryFullSlug!, match.params.testUuid!),
      'test_case_resource_exception',
      {},
      'json',
      SentryPipelinesTeam.Mangalyaan
    );

    return new TestCase(testCaseData, 0);
  },
});

export const loadFixFlakyTestStatusResource =
  createResource<FixFlakyTestStatus | null>({
    type: 'fixFlakyTestStatus',
    getKey: ({ match }) =>
      getFixFlakyTestStatusUrl(
        match.params.repositoryFullSlug!,
        match.params.testUuid!
      ),
    getData: async ({ match }) => {
      const fixFlakyTestStatusData = await fetchData(
        getFixFlakyTestStatusUrl(
          match.params.repositoryFullSlug!,
          match.params.testUuid!
        ),
        'fix_flaky_test_status_resource_exception',
        {},
        'json',
        SentryPipelinesTeam.Mangalyaan
      );

      const values = fixFlakyTestStatusData?.values;
      if (!Array.isArray(values) || !values.length) return null;

      const latest = values[0];
      const pipeline = latest?.pipeline;
      const pullRequest = latest?.pullrequest;

      return {
        actionTriggerUuid: latest?.action_trigger_uuid ?? '',
        createdAt: latest?.created_at ?? '',
        lastUpdatedAt: latest?.last_updated_at ?? '',
        status: latest?.status ?? '',
        createdByUser: latest?.created_by_user
          ? new User(latest.created_by_user)
          : new User(),
        pipeline: {
          type: pipeline?.type ?? '',
          uuid: pipeline?.uuid ?? '',
          buildNumber: pipeline?.build_number ?? 0,
        },
        ...(pullRequest?.id && {
          pullRequest: {
            id: pullRequest.id,
            link: pullRequest.links?.html?.href,
          },
        }),
      };
    },
  });
